<template>
	<div v-if="lening" class="flex flex-col">
		<LoanApplicationKeyValue
			:entries="{
				Hypotheeknummer: hypotheek.HypotheekNr,
				Leningnummer: lening.LeningNr,
				Hypotheekbedrag: `€ ${formatNumericWhole(lening.HypotheekBedrag.replace('.', ','))}`,
				Product: lening.ProductNaam,
			}"
		/>

		<span class="font-semibold text-[var(--text-primary)]"> Leningdelen </span>
		<AppGridTable
			:columns="leningdelenColumns"
			:rows="leningdelenRows"
			key-col="Volgnummer"
		/>
	</div>
</template>

<script setup lang="ts">
import { splitHdnValue } from "@/helpers/hdnHelpers";
import { computed, ref, watch } from "vue";
import { formatNumericWhole } from "@/helpers/inputHelpers";
import type { AppColumn, AppRow } from "@/models/AppTable";
import type { LoanApplicationStatusMessage } from "@/models/LoanApplicationStatusMessage";

const props = defineProps<{
	message: LoanApplicationStatusMessage;
}>();

const hypotheek = computed(() => {
	return (props.message.properties["passageData"] as any)?.Hypotheek ?? null;
});

const lening = computed(() => {
	return (
		(props.message.properties["passageData"] as any)?.Hypotheek?.Lening ?? null
	);
});

const leningdelenColumns = ref<AppColumn[]>([]);
const leningdelenRows = ref<AppRow[]>([]);

const allLeningdeelColumns: AppColumn[] = [
	{ header: "Nummer", accessorKey: "LeningdeelNr" },
	{
		header: "Hoogte",
		accessorKey: "LeningDeelBedrag",
		displayFn: (value) => `€ ${formatNumericWhole(value?.replace(".", ","))}`,
	},
	{
		header: "Looptijd",
		accessorKey: "DuurInMnd",
		displayFn: (value) => `${value} mnd.`,
	},
	{
		header: "Rentevast",
		accessorKey: "RenteVastInMnd",
		displayFn: (value) => `${value} mnd.`,
	},
	{
		header: "Rente",
		accessorKey: "RentePct",
		displayFn: (value) => `${value}%`,
	},
	{
		header: "Renteafspraak",
		accessorKey: "RenteAfspraak",
		displayFn: (value) => splitHdnValue(value)[1],
	},
	{
		header: "Hypotheekvorm",
		accessorKey: "AflossingsVorm",
		displayFn: (value) => splitHdnValue(value)[1],
	},
	{ header: "Product", accessorKey: "ProductNaam" },
	{
		header: "Betalingstermijn",
		accessorKey: "BetalingsTermijn",
		displayFn: (value) => splitHdnValue(value)[1],
	},
];

watch(
	lening,
	(lening) => {
		const leningdelen = lening?.Leningdeel;
		if (!leningdelen) {
			leningdelenColumns.value = [];
			leningdelenRows.value = [];
			return;
		}

		const columnsToShow: Record<string, boolean> = {};

		leningdelenRows.value = leningdelen.map((deel: any) => {
			const leningdeel: Record<string, any> = {};

			allLeningdeelColumns.forEach(({ accessorKey }) => {
				if (deel[accessorKey]) {
					columnsToShow[accessorKey] = true;
					leningdeel[accessorKey] = deel[accessorKey];
				}
			});
			return leningdeel;
		});

		leningdelenColumns.value = allLeningdeelColumns.filter(
			({ accessorKey }) => columnsToShow[accessorKey],
		);
	},
	{ immediate: true },
);
</script>
